import Form from "views/Form";
import className from "classnames";
import { actions, parseError } from "utils/helpers";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import { selectAuthUser } from "features/auth/authSlice";
import { formConfig } from "pages/CallCenter/CallCenterAgents/UpdateCenterAgent/formConfig";
import { setNavbarTitle } from "features/navbar/navbarSlice";
import { useEffect, useLayoutEffect, useState } from "react";
import FormElement from "components/Form/FormElement/FormElement";
import { createSearchParams, useNavigate, useParams } from "react-router-dom";
import { searchHandler } from "models/search";
import { selectAllResults } from "features/search/searchSlice";
import { dropdownItemSelector } from "models/form";
import cardStyles from "styles/modules/Card.module.scss";
import formStyles from "components/Form/Form.module.scss";
import utilityStyles from "styles/modules/Utility.module.scss";
import FormDropdown from "components/Form/FormDropdown/FormDropdown";
import { useGetCallCenterAgentsQuery, useUpdateCallCenterAgentsMutation } from "features/call_center/callCenterApiSlice";

const form = new Form(formConfig);

const UpdateCenterAgent = ({ label }) => {

  const [activeInput, setActiveInput] = useState("");
  const params = useParams();

  const { data: centerAgent } = useGetCallCenterAgentsQuery(params?.id);

  const authUser = useSelector(selectAuthUser);

  const [formElements, setFormElements] = useState([]);
  const [formValidity, setFormValidity] = useState(true);
  const [isChangesMade, setIsChangesMade] = useState(false);
  const [initialDataValues, setInitialDataValues] = useState([]);

  const searchResults = useSelector(selectAllResults);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [updateAgent, { isLoading }] = useUpdateCallCenterAgentsMutation();

  useEffect(() => {
    dispatch(setNavbarTitle("Edit Call Center Agent"));
  }, [dispatch]);

  useLayoutEffect(() => {
    form.createFormElements();
    setFormElements(form.formElements);
  }, []);

  useEffect(() => {
    if (centerAgent) {
      const dataValues = [];
      const updatedFormConfig = JSON.parse(JSON.stringify(formConfig));

      Object.entries(centerAgent).forEach(([key, value]) => {
        if (updatedFormConfig[key]) {
          if (key === "user_id" && centerAgent.user_id) {
            updatedFormConfig[key].elementConfig.value =
              centerAgent.user?.firstname; 
            updatedFormConfig[key].elementConfig["data-id"] = value; // Keep the ID for API submission
          } else if (key === "domain_id" && centerAgent.account) {
            updatedFormConfig[key].elementConfig.value =
              centerAgent.domain.name;
            updatedFormConfig[key].elementConfig["data-id"] = value; // Keep the ID for API submission
          } else {
            updatedFormConfig[key].elementConfig.value = value;
          }
          dataValues.push(String(value));
        }

        return null;
      });

      form.setConfig(updatedFormConfig);
      form.createFormElements();
      setFormElements(form.formElements);
      setInitialDataValues(dataValues);
    }
  }, [centerAgent]);

  useEffect(() => {
    return () => {
      if (process.env.NODE_ENV === "production") {
        form.clear(); // will run once in production
        form.setConfig(formConfig);
      }
    };
  }, []);

  const submitFormHandler = async (e) => {
    e.preventDefault();

    const formData = {};

    if (formValidity && authUser?.actions[label].includes(actions.update)) {
      for (const key in form.getFormConfig()) {
        const el = form.getFormConfig()[key];

        if (el.elementConfig.type === "number") {
          formData[key] = form.parseFormValue(el.elementConfig.value, "number");
        } else {
          formData[key] = el.elementConfig.value;
        }
      }

      try {
        await updateAgent({
          centerAgentID: centerAgent.id,
          ...formData,
        }).unwrap();
        toast.success("Call Center Agent updated successfully");
        navigate({
          pathname: "/call_center/agents/",
          search: createSearchParams({ page: "1" }).toString(),
        });
        window.location.reload();
      } catch (error) {
        const errorMessage = parseError(error);
        toast.error(errorMessage);
      }
    }
  };

  const dropdownItemClickHandler = (key, item) => {
    dropdownItemSelector(key, item, form);
    setFormElements(form.formElements);
    setActiveInput("");
  };

  const inputChangeHandler = (e) => {
    const updatedInput = form.inputChangeHandler(e);

    if (updatedInput) {
      let isFormChanged = true;
      const updatedFormValues = [];

      updatedFormValues.push(e.target.value);

      if (e.target.id === "user_id") {
        searchHandler("/users", e.target.value, dispatch);
      }
      if (e.target.id === "domain_id") {
        searchHandler("/domains", e.target.value, dispatch);
      }

      form.updateFormElement({ id: e.target.id, config: updatedInput });

      form.checkFormValidity();

      isFormChanged =
        updatedFormValues.every((value) => initialDataValues.includes(value)) &&
        isFormChanged;

      setFormElements(form.formElements);
      setFormValidity(form.isValid);
      setIsChangesMade(!isFormChanged);
    }
  };

  let buttonEl = null;

  if (authUser?.actions[label].includes(actions.update)) {
    buttonEl = (
      <div className={formStyles.btn}>
        <button disabled={isLoading || !formValidity || !isChangesMade}>
          {isLoading ? "Saving..." : "Save Changes"}
        </button>
      </div>
    );
  }

  return (
    <div
      className={`${utilityStyles.flexColumn} ${utilityStyles.itemsCenter} ${utilityStyles.justifyCenter}`}
    >
      <div
        className={className({
          [cardStyles.menu]: true,
          [utilityStyles.wFull]: true,
        })}
      >
        <h3>Changes made to this call center agent will overwrite the existing info</h3>
      </div>
      <div className={formStyles.wrapper}>
        <form className={formStyles.container} onSubmit={submitFormHandler}>
          {formElements.map((el, idx) => {
            const { title, elementType, elementConfig, ...config } = el.config;

            return (
              <div key={`el-group-${idx + 1}`} className={formStyles.group}>
                <FormElement
                  {...config}
                  title={title}
                  type={elementType}
                  id={elementConfig.id}
                  config={{
                    ...elementConfig,
                    onChange: inputChangeHandler,
                  }}
                />
                <FormDropdown
                  items={searchResults}
                  itemClickHandler={(item) =>
                    dropdownItemClickHandler(elementConfig.id, item)
                  }
                  visible={
                    activeInput === elementConfig.id && el.config.dropdown
                  }
                />
              </div>
            );
          })}
          {buttonEl}
        </form>
      </div>
    </div>
  );
};

export default UpdateCenterAgent;
